import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, CloudUpload } from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import {
  AddingCrewMemberDialogProps,
  RoleSkillTool,
} from "../types/add-crew-member";
import CustomTextField from "../../../components/common/Textfield";
import PhoneNumberField from "../../../components/common/PhoneNumberField";
import useDebounce from "../../../hooks/useDebounce";
import { tokens } from "../../../theme";
import { DocumentIcon } from "../../../components/icons";
import CardSelector from "../../../components/common/CardSelector";

import { countries } from "../../../data/countries";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const AddCrewMemberDialog: React.FC<AddingCrewMemberDialogProps> = ({
  addMemberOpen,
  formData,
  user,
  formLoading,
  handleAddCrewMember,
  handleEmergencyContactChange,
  handleInputChange,
  handlePhoneChange,
  onCountryChange,
  onRolesChange,
  onSkillsChange,
  onToolsChange,
  resetForm,
  selectedCountry,
  selectedRoles,
  selectedSkills,
  selectedTools,
  setAddMemberOpen,
  setFormData,
}) => {
  const [roleOptions, setRoleOptions] = useState<RoleSkillTool[]>([]);
  const [skillOptions, setSkillOptions] = useState<RoleSkillTool[]>([]);
  const [toolOptions, setToolOptions] = useState<RoleSkillTool[]>([]);
  const [roleValue, setRoleValue] = useState("");
  const [skillValue, setSkillValue] = useState("");
  const [toolValue, setToolValue] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [pilotDocuments, setPilotDocuments] = useState<string[]>([]);

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [countryValue, setCountryValue] = useState("");
  const [countriesLoading, setCountriesLoading] = useState(false);

  useEffect(() => {
    setCountriesLoading(true);
    if (countries) {
      setCountryOptions(countries);
    }
    setCountriesLoading(false);
  }, []);

  const debouncedRoleSearch = useDebounce(roleValue, 400);
  const debouncedSkillSearch = useDebounce(skillValue, 400);
  const debouncedToolSearch = useDebounce(toolValue, 400);

  const fetchRoles = useCallback(async (roleSearching: string) => {
    setRolesLoading(true);
    try {
      const params: Record<string, string | number> = { page_size: 20 };
      if (roleSearching) {
        params.search = roleSearching;
      }
      const roles = await apiService.get<{ results: RoleSkillTool[] }>(
        "/api/v1/crews/roles/",
        params,
      );
      setRoleOptions(roles.data.results || []);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const fetchSkills = useCallback(async (skillSearching: string) => {
    setSkillsLoading(true);
    try {
      const params: Record<string, string | number> = { page_size: 20 };
      if (skillSearching) {
        params.search = skillSearching;
      }
      const skills = await apiService.get<{ results: RoleSkillTool[] }>(
        "/api/v1/crews/skills/",
        params,
      );
      setSkillOptions(skills.data.results || []);
    } catch (err) {
      console.error("Failed to fetch skills:", err);
      setSkillOptions([]);
    } finally {
      setSkillsLoading(false);
    }
  }, []);

  const fetchTools = useCallback(async (toolSearching: string) => {
    setToolsLoading(true);
    try {
      const params: Record<string, string | number> = { page_size: 20 };
      if (toolSearching) {
        params.search = toolSearching;
      }
      const tools = await apiService.get<{ results: RoleSkillTool[] }>(
        "/api/v1/crews/tools/",
        params,
      );
      setToolOptions(tools.data.results || []);
    } catch (err) {
      console.error("Failed to fetch tools:", err);
      setToolOptions([]);
    } finally {
      setToolsLoading(false);
    }
  }, []);

  const removeDocument = (docToRemove: string) => {
    setPilotDocuments(pilotDocuments.filter((doc) => doc !== docToRemove));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newDocs = Array.from(files).map((file) => file.name);
      setPilotDocuments([...pilotDocuments, ...newDocs]);
    }
  };

  useEffect(() => {
    if (user) {
      console.log({ user });
      setFormData({
        existingUser: true,
        user_id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        username: user.username,
        password: "tilapia123",
        phone: user.phone_number || "",
        role: user.role,
        organization: user.organization || "",
        street_address: user.street_address || "",
        city: user.city || "",
        state: user.state_province || "",
        postal_code: user.postal_code || "",
        residence_country: user.residence_country || "",
        emergency_contact: user.emergency_contact || "",
        emergency_contact_phone: user.emergency_contact_phone || "",
        date_of_birth: user.date_of_birth || "",
        occupation: user.occupation || "",
        marital_status: user.marital_status || null,
        national_id: user.national_id || "",
        roles: [],
        skills: [],
        tools: [],
        preferences: user.service_preferences || "",
        experience_years: 0,
      });
    }
  }, [user, setFormData]);

  useEffect(() => {
    fetchRoles(debouncedRoleSearch);
  }, [debouncedRoleSearch, fetchRoles]);

  useEffect(() => {
    fetchSkills(debouncedSkillSearch);
  }, [debouncedSkillSearch, fetchSkills]);

  useEffect(() => {
    fetchTools(debouncedToolSearch);
  }, [debouncedToolSearch, fetchTools]);

  return (
    <Dialog
      open={addMemberOpen}
      onClose={() => {
        setAddMemberOpen(false);
        resetForm();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Add new crew member
        <IconButton
          onClick={() => {
            setAddMemberOpen(false);
            resetForm();
          }}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={formLoading}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="create new pilot tabs"
              sx={{
                mb: 1,
                ".MuiTab-root": {
                  color: colors.primary[300],
                },
                ".Mui-selected": {
                  color: colors.primary[400],
                },
              }}
            >
              <Tab label="Basic Details" />
              <Tab label="Pilot Details" />
              <Tab label="Skills & Tools" />
              <Tab label="Documents" />
            </Tabs>
          </Box>

          {/* Basic Details */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <CustomTextField
                  name="first_name"
                  label="First Name"
                  placeholder="Enter first name"
                  fullWidth
                  value={formData.first_name}
                  onChange={(e) =>
                    handleInputChange("first_name", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="last_name"
                  label="Last Name"
                  placeholder="Enter last name"
                  fullWidth
                  value={formData.last_name}
                  onChange={(e) =>
                    handleInputChange("last_name", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="email"
                  label="Email Address"
                  placeholder="Enter email address"
                  fullWidth
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="username"
                  label="Username"
                  placeholder="Enter username"
                  fullWidth
                  value={formData.username}
                  onChange={(e) =>
                    handleInputChange("username", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={12}>
                <CustomTextField
                  name="password"
                  label="Password"
                  placeholder="Enter password"
                  fullWidth
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={12}>
                <PhoneNumberField
                  fullWidth
                  label="Phone Number"
                  placeholder="Phone Number. e.g. +263712345678"
                  value={formData.phone || ""}
                  onChange={handlePhoneChange}
                  defaultCountry="ZW"
                  variant="outlined"
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={12}>
                <CardSelector
                  items={roleOptions.map((role) => ({
                    id: role.id,
                    name: role.name,
                    description: role.description || undefined,
                  }))}
                  selectedItems={selectedRoles.map((role) => ({
                    id: role.id,
                    name: role.name,
                    description: role.description || undefined,
                  }))}
                  onChange={(items) =>
                    onRolesChange(
                      items.map(
                        (item) =>
                          roleOptions.find((r) => r.id === item.id) || roleOptions[0]
                      )
                    )
                  }
                  label="Roles"
                  searchPlaceholder="Search roles..."
                  loading={rolesLoading}
                  multiSelect={true}
                  onSearchChange={(value) => setRoleValue(value)}
                />
              </Grid>
              <Grid size={12}>
                <CustomTextField
                  name="organization"
                  label="Organization"
                  placeholder="Enter your organization's name"
                  fullWidth
                  value={formData.organization}
                  onChange={(e) =>
                    handleInputChange("organization", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Pilot Details */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <CustomTextField
                  name="street_address"
                  label="Street Address"
                  placeholder="Enter street address"
                  fullWidth
                  value={formData.street_address}
                  onChange={(e) =>
                    handleInputChange("street_address", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="city"
                  label="City"
                  placeholder="Enter city"
                  fullWidth
                  value={formData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="state"
                  label="State"
                  placeholder="Enter state"
                  fullWidth
                  value={formData.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="postal_code"
                  label="Postal Code"
                  placeholder="Enter postal code"
                  fullWidth
                  value={formData.postal_code}
                  onChange={(e) =>
                    handleInputChange("postal_code", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  multiple={false}
                  options={countryOptions}
                  getOptionLabel={(option) => {
                    const country = option ? option : "Country Not Found";
                    return country;
                  }}
                  value={selectedCountry}
                  onChange={(_, newValue) => onCountryChange(newValue)}
                  inputValue={countryValue}
                  onInputChange={(_, newInput) => setCountryValue(newInput)}
                  loading={countriesLoading}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      placeholder="Search country..."
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {countriesLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={() => null}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="date_of_birth"
                  label="Date of Birth"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  fullWidth
                  type="date"
                  variant="outlined"
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    handleInputChange("date_of_birth", e.target.value)
                  }
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="national_id"
                  label="National ID"
                  placeholder="Enter National ID"
                  fullWidth
                  value={formData.national_id}
                  onChange={(e) =>
                    handleInputChange("national_id", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="occupation"
                  label="Occupation"
                  placeholder="Enter Occupation"
                  fullWidth
                  value={formData.occupation}
                  onChange={(e) =>
                    handleInputChange("occupation", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  fullWidth
                  label="Marital Status"
                  name="marital_status"
                  value={formData.marital_status}
                  select
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      marital_status: e.target.value,
                    })
                  }
                >
                  <MenuItem value="" disabled sx={{ color: "#aaa" }}>
                    Select Marital Status
                  </MenuItem>
                  <MenuItem value="single">Single</MenuItem>
                  <MenuItem value="married">Married</MenuItem>
                  <MenuItem value="divorced">Divorced</MenuItem>
                  <MenuItem value="widowed">Widowed</MenuItem>
                </CustomTextField>
              </Grid>
              <Grid size={6}>
                <PhoneNumberField
                  fullWidth
                  label="Emergency Contact"
                  placeholder="E.g. +263712345678"
                  value={formData.emergency_contact_phone || ""}
                  onChange={handleEmergencyContactChange}
                  defaultCountry="ZW"
                  name="emergency_contact_phone"
                  variant="outlined"
                  disabled={formLoading}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="emergency_contact"
                  label="Emergency Contact"
                  placeholder="Emergency Contact Name"
                  fullWidth
                  value={formData.emergency_contact}
                  onChange={(e) =>
                    handleInputChange("emergency_contact", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Skills & Tools */}
          <TabPanel value={tabValue} index={2}>
            <Grid container>
              <Grid size={12}>
                <CustomTextField
                  name="preference"
                  label="Preferences"
                  placeholder="Enter job preferences"
                  fullWidth
                  value={formData.preferences}
                  onChange={(e) =>
                    handleInputChange("preferences", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
              <Grid
                size={12}
                sx={{
                  borderBottom: "1px solid",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  my: 2,
                }}
              >
                <CardSelector
                  items={skillOptions.map((skill) => ({
                    id: skill.id,
                    name: skill.name,
                    description: skill.description || undefined,
                  }))}
                  selectedItems={selectedSkills.map((skill) => ({
                    id: skill.id,
                    name: skill.name,
                    description: skill.description || undefined,
                  }))}
                  onChange={(items) =>
                    onSkillsChange(
                      items.map(
                        (item) =>
                          skillOptions.find((s) => s.id === item.id) || skillOptions[0]
                      )
                    )
                  }
                  label="Pilot Skills"
                  searchPlaceholder="Search skills..."
                  loading={skillsLoading}
                  multiSelect={true}
                  onSearchChange={(value) => setSkillValue(value)}
                />
              </Grid>
              <Grid
                size={12}
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  mb: 2,
                }}
              >
                <CardSelector
                  items={toolOptions.map((tool) => ({
                    id: tool.id,
                    name: tool.name,
                    description: tool.description || undefined,
                  }))}
                  selectedItems={selectedTools.map((tool) => ({
                    id: tool.id,
                    name: tool.name,
                    description: tool.description || undefined,
                  }))}
                  onChange={(items) =>
                    onToolsChange(
                      items.map(
                        (item) =>
                          toolOptions.find((t) => t.id === item.id) || toolOptions[0]
                      )
                    )
                  }
                  label="Pilot Work Equipment"
                  searchPlaceholder="Search equipment..."
                  loading={toolsLoading}
                  multiSelect={true}
                  onSearchChange={(value) => setToolValue(value)}
                />
              </Grid>
              <Grid size={12}>
                <CustomTextField
                  name="experience_years"
                  label="Years Experience"
                  placeholder="Enter years of experience"
                  fullWidth
                  value={formData.experience_years}
                  onChange={(e) =>
                    handleInputChange("experience_years", e.target.value)
                  }
                  disabled={formLoading}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Documents */}
          <TabPanel value={tabValue} index={3}>
            <Grid container>
              <Grid size={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {`[NB: Non Functional At The Moment]`}
                </Typography>
                <Paper
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    mb: 3,
                    backgroundColor: colors.primary[800],
                  }}
                >
                  <IconButton component="label">
                    <CloudUpload sx={{ fontSize: 48, color: "text.secondary" }} />
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={handleFileUpload}
                    />
                  </IconButton>
                  <Typography variant="body1">
                    Drag and drop files here, or click to select files
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Uploaded Documents
                </Typography>
                {pilotDocuments?.map((doc, index) => {
                  return (
                    <Box
                      display={"flex"}
                      key={index}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      sx={{
                        padding: 1,
                        mb: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: backgroundColor,
                      }}
                    >
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            padding: 1,
                            bgcolor: backgroundColor,
                          }}
                        >
                          <DocumentIcon
                            color="disabled"
                            sx={{ fontSize: 20 }}
                          />
                        </Box>
                        <Typography variant={"body2"}>{doc}</Typography>
                      </Box>
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        <IconButton onClick={() => removeDocument(doc)}>
                          <Close sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            setAddMemberOpen(false);
            resetForm();
          }}
          disabled={formLoading}
          sx={{
            color: colors.primary[100],
            backgroundColor: colors.primary[800],
          }}
        >
          Cancel
        </Button>
        {tabValue !== 3 ? (
          <Button
            variant="contained"
            onClick={() => setTabValue(tabValue + 1)}
            disabled={formLoading}
            sx={{
              backgroundColor: colors.primary[400],
              transition: ".4s ease-in-out",
            }}
          >
            {formLoading && <CircularProgress size={20} sx={{ mr: 1 }} />} Next
            Form
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleAddCrewMember}
            disabled={formLoading}
            sx={{
              backgroundColor: colors.primary[400],
              transition: ".4s ease-in-out",
            }}
          >
            {formLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}{" "}
            Create New Pilot
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCrewMemberDialog;
